import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from "@simplewebauthn/browser";
import { z } from "zod";

import { API } from "@js/api";
import type {
  AccountType,
  ActiveScreening,
  UnverifiedUser,
} from "@js/types/auth";
import type { CompanyNode } from "@js/types/common";
import type { EmployerMinimal } from "@js/types/employer";

import type { ClientReferralProgramFormValues } from "../dashboard/views/refer-clients-program/join-clients-referral-program-form";

type BanUserFromPostingParams = {
  userId: number;
  postId: number;
};
type SendConnectorSignupDataParams = {
  page_name: "Earn";
};

const userSearchSchema = z.object({
  id: z.number(),
  name_and_email: z.string(),
});

export type UserSearchResult = z.infer<typeof userSearchSchema>;

export type UserSearchParams = {
  search?: string;
  return_ops_assigned_to_jobs_only?: boolean;
  hiring_manager?: boolean;
  page_size?: number;
  for_employer?: number;
  invoice_action_performer?: boolean;
  return_sales_only?: boolean;
  return_ops_only?: boolean;
};

const usersApi = API.injectEndpoints({
  endpoints: (build) => ({
    getUserSearch: build.query<UserSearchResult[], UserSearchParams>({
      query: (params) => ({
        url: `/user_search/`,
        method: "GET",
        params: {
          limit: 100,
          ...params,
        },
      }),
    }),
    getUserSearchItem: build.query<UserSearchResult | undefined, number>({
      query: (id) => ({
        url: `/user_search/${id}/`,
        method: "GET",
        validateStatus: () => true,
      }),
      transformResponse: (response: unknown) => {
        // validate the data as we are omitting the status validation
        const parseResult = userSearchSchema.safeParse(response);
        if (!parseResult.success) {
          return undefined;
        }

        return parseResult.data;
      },
    }),
    banUserFromPosting: build.mutation<void, BanUserFromPostingParams>({
      query: ({ userId, postId }) => ({
        url: `/users/${userId}/ban_from_posting/`,
        method: "POST",
        data: {
          post: postId,
        },
      }),
    }),
    sendEmployerOnboardingData: build.mutation<void, void>({
      query: () => ({
        url: `/users/send_employer_onboarding_data/`,
        method: "POST",
      }),
    }),
    sendConnectorSignupData: build.mutation<
      void,
      SendConnectorSignupDataParams
    >({
      query: ({ page_name }) => ({
        url: `/users/send_connector_signup_data/?page_name=${page_name}`,
        method: "POST",
      }),
    }),
    getUnfinishedUser: build.query<UnverifiedUser, string>({
      query: (key) => ({
        url: `/user/registration/check_key/`,
        method: "GET",
        params: {
          confirmation_key: key,
        },
      }),
      keepUnusedDataFor: 0,
    }),
    optInIntoReferClientProgram: build.mutation<
      void,
      ClientReferralProgramFormValues
    >({
      query: (values) => ({
        url: `/users/optin_into_refer_client_program/`,
        method: "POST",
        data: values,
      }),
    }),
    getActiveScreening: build.query<ActiveScreening, void>({
      query: () => ({
        url: `/users/active_screening/`,
        method: "GET",
      }),
      keepUnusedDataFor: 1,
    }),
  }),
});

export const {
  useBanUserFromPostingMutation,
  useSendConnectorSignupDataMutation,
  useSendEmployerOnboardingDataMutation,
  useGetUnfinishedUserQuery,
  useOptInIntoReferClientProgramMutation,
  useGetUserSearchQuery,
  useGetUserSearchItemQuery,
  useLazyGetUserSearchItemQuery,
  useGetActiveScreeningQuery,
  useLazyGetActiveScreeningQuery,
} = usersApi;

export type NewClientInvitationDetails = {
  first_name: string;
  last_name: string;
  email: string;
  organization_name: string | null;
  company_node?: CompanyNode;
  invitation_error: string | null;
  invitation_type: string | null;
};

export type NewFreelancerInvitationDetails = {
  first_name: string;
  last_name: string;
  email: string;
  invitation_error: string | null;
  invitation_type: string | null;
};

export type GetPublicInvitationDetailsResponse = {
  account_type: AccountType;

  avatar: string;
  avatar_thumbnail: string;
  created: string;
  first_name: string;
  freelancer_approved: boolean;
  gravatar: string;

  has_avatar_set: boolean;
  id: number;
  invitation_error: string;
  invitation_type: string;
  last_name: string;
  profile_url: string;
  public_name: string;
  title: string;

  job?: {
    budget_maximum_usd: string;
    budget_minimum_usd: string;
    employer: EmployerMinimal & { link: string; full_link: string };
    payment_type: EnumType<typeof ENUMS.JobPaymentType>;
    title: string;
    role: string;
    is_open: boolean;
  };
};

const invitationsApi = API.injectEndpoints({
  endpoints: (build) => ({
    getClientInvitationDetails: build.query<
      NewClientInvitationDetails,
      { key: string }
    >({
      query: ({ key }) => ({
        url: `/users/new_employer_invitation_details/?new_employer_invitation_key=${key}`,
        method: "GET",
      }),
    }),
    getFreelancerInvitationDetails: build.query<
      NewFreelancerInvitationDetails,
      { key: string }
    >({
      query: ({ key }) => ({
        url: `/users/new_freelancer_invitation_details/?freelancer_invitation_key=${key}`,
        method: "GET",
      }),
    }),
    getPublicInvitationDetails: build.query<
      GetPublicInvitationDetailsResponse,
      {
        invitation_key?: string;
        job_id?: string;
        referrer?: string;
      }
    >({
      query: (params) => ({
        url: `/users/public_invitation_details/`,
        method: "GET",
        params,
      }),
    }),
  }),
});

export const {
  useGetFreelancerInvitationDetailsQuery,
  useGetClientInvitationDetailsQuery,
  useGetPublicInvitationDetailsQuery,
} = invitationsApi;

export type Passkey = {
  id: number;
  name: string;
  created_at: string;
  last_used_at: string | null;
};

export type PasskeyConfirmation =
  | { current_password: string }
  | { code: string; is_backup_code: boolean }
  | { key: string };

type DeletePasskeyParams = {
  id: number;
  confirmation: PasskeyConfirmation;
};

type CompletePasskeyRegistrationParams = {
  credential: RegistrationResponseJSON;
  confirmation: PasskeyConfirmation;
};

type RenamePasskeyParams = {
  id: number;
  name: string;
};

const passkeysApi = API.injectEndpoints({
  endpoints: (build) => ({
    getPasskeys: build.query<Passkey[], void>({
      query: () => ({
        url: `/passkeys/`,
        method: "GET",
      }),
      providesTags: ["Passkeys"],
    }),
    beginPasskeyRegistration: build.mutation<
      PublicKeyCredentialCreationOptionsJSON,
      void
    >({
      query: () => ({
        url: `/webauthn/registration/begin/`,
        method: "POST",
      }),
    }),
    completePasskeyRegistration: build.mutation<
      void,
      CompletePasskeyRegistrationParams
    >({
      query: ({ credential, confirmation }) => ({
        url: `/webauthn/registration/complete/`,
        method: "POST",
        data: { ...credential, ...confirmation },
      }),
      invalidatesTags: ["Passkeys"],
    }),
    deletePasskey: build.mutation<void, DeletePasskeyParams>({
      query: ({ id, confirmation }) => ({
        url: `/passkeys/${id}/`,
        method: "DELETE",
        data: confirmation,
      }),
      invalidatesTags: ["Passkeys"],
    }),
    renamePasskey: build.mutation<Passkey, RenamePasskeyParams>({
      query: ({ id, name }) => ({
        url: `/passkeys/${id}/`,
        method: "PATCH",
        data: { name },
      }),
      invalidatesTags: ["Passkeys"],
    }),
    beginPasskeyLogin: build.mutation<
      PublicKeyCredentialRequestOptionsJSON,
      void
    >({
      query: () => ({
        url: `/webauthn/authentication/begin/`,
        method: "POST",
      }),
    }),
    completePasskeyLogin: build.mutation<void, AuthenticationResponseJSON>({
      query: (assertion) => ({
        url: `/webauthn/authentication/complete/`,
        method: "POST",
        data: assertion,
      }),
    }),
    beginPasskeyStepUp: build.mutation<
      PublicKeyCredentialRequestOptionsJSON,
      void
    >({
      query: () => ({
        url: `/passkeys/step-up/begin/`,
        method: "POST",
      }),
    }),
    completePasskeyStepUp: build.mutation<
      { key: string },
      AuthenticationResponseJSON
    >({
      query: (assertion) => ({
        url: `/passkeys/step-up/complete/`,
        method: "POST",
        data: assertion,
      }),
    }),
  }),
});

export const {
  useGetPasskeysQuery,
  useBeginPasskeyRegistrationMutation,
  useCompletePasskeyRegistrationMutation,
  useDeletePasskeyMutation,
  useRenamePasskeyMutation,
  useBeginPasskeyLoginMutation,
  useCompletePasskeyLoginMutation,
  useBeginPasskeyStepUpMutation,
  useCompletePasskeyStepUpMutation,
} = passkeysApi;
